import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import {
  Square2StackIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import toast from "react-hot-toast";
import { reportsApi } from "../../api/reports";
import { campersApi } from "../../api/campers";
import type { CamperKerchiefData, KerchiefActivity, Camper } from "../../types";
import { computeClockwiseSlots } from "../../utils/kerchiefSlots";


// ─── Star outline (graduation) ────────────────────────────────────────────────

function StarOutline({ size = 14 }: { size?: number }) {
  const pts =
    "0,-1 0.225,-0.309 0.951,-0.309 0.363,0.118 0.588,0.809 " +
    "0,0.382 -0.588,0.809 -0.363,0.118 -0.951,-0.309 -0.225,-0.309";
  return (
    <svg width={size} height={size} viewBox="-1.1 -1.1 2.2 2.2">
      <polygon
        points={pts}
        fill="none"
        stroke="black"
        strokeWidth="0.22"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Slot layout geometry is shared with the admin Kerchief Layout editor —
// see utils/kerchiefSlots.ts for the triangle slot math.

// Reference kerchief dimensions used to convert pixel offsets → CSS%
// for triangle-bounds checking. Sized larger than the badge nodes so the
// kerchief reads as a physical cloth with generous red space around and
// between badges. Users zoom to fit.
const W_REF = 4000;
const H_REF = 2000; // W_REF / 2

// Badge geometry. PATCH_SIZE drives the rest of the per-node layout —
// stamp offsets, ribbon dimensions, and the slot wrapper width all scale
// with it. SCALE is relative to the original 38px design so existing
// pixel constants stay readable.
const PATCH_SIZE = 144;
const PATCH_R    = PATCH_SIZE / 2;
const SCALE      = PATCH_SIZE / 38;
// Stamps render slightly smaller than full badge proportion so the
// kerchief reads as a physical cloth where stamps are minor marks
// against the larger embroidered patches.
const STAMP_SCALE = SCALE * 0.7;
const NODE_W     = Math.round(72 * SCALE); // wrapper width (centered on slot)
const NODE_H     = PATCH_SIZE;             // patch sits at top of node
const CENTER_X   = NODE_W / 2;
const CENTER_Y   = PATCH_R;

// Padded canvas around the cloth so stamps that scatter past the
// triangle's diagonal edges have visible space to land on. Tuned
// roughly to PATCH_SIZE so the border scales with the badges.
const CANVAS_BORDER_X = Math.round(PATCH_SIZE * 0.6);
const CANVAS_BORDER_Y = Math.round(PATCH_SIZE * 0.4);

// ─── Deterministic pseudo-random (stable across renders) ─────────────────────

function seededRand(seed: number): number {
  const x = Math.sin(seed + 1) * 43758.5453;
  return x - Math.floor(x);
}

// ─── Stamp types ──────────────────────────────────────────────────────────────

type StampDef =
  | { kind: "year"; year: number; count: number }
  | { kind: "honor" }
  | { kind: "graduation" };

function buildStamps(a: KerchiefActivity): StampDef[] {
  // Group AC completions by year so a single year on a kerchief renders
  // as one cluster (count copies of the same year tag); distinct years
  // scatter individually for an organic, hand-stamped look.
  const yearCounts = new Map<number, number>();
  for (const y of a.completionYears) {
    yearCounts.set(y, (yearCounts.get(y) ?? 0) + 1);
  }
  const stamps: StampDef[] = [];
  for (const [year, count] of yearCounts) {
    stamps.push({ kind: "year", year, count });
  }
  // Honors accumulate — push one stamp per honor record so a camper with
  // multiple honors in the same activity gets multiple "H" stamps.
  for (let i = 0; i < a.honors.length; i++) stamps.push({ kind: "honor" });
  if (a.graduated)  stamps.push({ kind: "graduation" });
  return stamps;
}

function formatYear(y: number): string { return `'${String(y).slice(2)}`; }

// ─── Stamp elements ───────────────────────────────────────────────────────────

function YearBadge({ year }: { year: number }) {
  return (
    <span
      className="inline-block font-mono leading-none select-none"
      style={{
        // Black ink stamp directly on the red cloth — no chip, no border.
        fontSize: 11 * STAMP_SCALE,
        color: "rgba(0,0,0,0.92)",
        fontWeight: 900,
        letterSpacing: "0.04em",
        // Slight ink-bleed so glyph edges feel pressed into fabric.
        textShadow: "0 0 1px rgba(0,0,0,0.55)",
      }}
    >
      {formatYear(year)}
    </span>
  );
}

// Cluster of N year tags representing N AC completions in the same year.
// Same-year stamps render together as a single grid block — never mixed
// with other years. Cols adapt to count so very-high-count clusters wrap
// into a roughly square block instead of one long row.
function YearStack({
  year,
  count,
  seed,
}: { year: number; count: number; seed: number }) {
  if (count === 1) return <YearBadge year={year} />;
  const cols = count <= 3 ? count : count <= 6 ? 2 : 3;
  const tilt = (seededRand(seed) - 0.5) * 14;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, auto)`,
        gap: `${1 * STAMP_SCALE}px`,
        transform: `rotate(${tilt}deg)`,
        transformOrigin: "center",
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <YearBadge key={i} year={year} />
      ))}
    </div>
  );
}

function HonorBadge() {
  return (
    <span
      className="inline-block leading-none select-none"
      style={{
        fontSize: 14 * STAMP_SCALE,
        color: "rgba(0,0,0,0.92)",
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontWeight: 900,
        letterSpacing: "0.04em",
        textShadow: "0 0 1px rgba(0,0,0,0.55)",
      }}
    >
      H
    </span>
  );
}

function GradBadge() {
  return <StarOutline size={14 * STAMP_SCALE} />;
}

// ─── Stamp scatter: biased toward outside edge of each region ────────────────
//
// Icon circle: center (CENTER_X, CENTER_Y) in ActivityNode coords, radius
// PATCH_R. Stamps (top-left corner at (CENTER_X+dx, CENTER_Y+dy)) may
// penetrate the icon up to 20% of its diameter past the circle edge.
//
// Ranges below are expressed in the original 38px-patch design and scaled
// by SCALE so the stamps' visual relationship to the badge is preserved
// at any PATCH_SIZE.

function stampOffset(
  seed: number,
  region: "top" | "left" | "right",
): { dx: number; dy: number } {
  const r1 = seededRand(seed);
  const r2 = seededRand(seed + 1);
  const r3 = seededRand(seed + 2);
  const outside = r1 < 0.8;

  if (region === "top") {
    const dx = (r2 - 0.5) * 72 * SCALE;                                       // ±36 horizontal
    const dy = (outside ? -(r3 * 30 + 30) : r3 * 18 + 12) * SCALE;            // [-60,-30] | [+12,+30]
    return { dx, dy };
  }
  if (region === "left") {
    const dy = (r2 - 0.5) * 72 * SCALE;                                       // ±36 vertical
    const dx = (outside ? -(r3 * 30 + 36) : r3 * 18 + 12) * SCALE;            // [-66,-36] | [+12,+30]
    return { dx, dy };
  }
  // right
  const dy = (r2 - 0.5) * 72 * SCALE;
  const dx = (outside ? r3 * 30 + 12 : -(r3 * 18 + 36)) * SCALE;              // [+12,+42] | [-54,-36]
  return { dx, dy };
}

// ─── Stamp collision detection ────────────────────────────────────────────────
//
// Conservative bounding box used for all stamp types; prevents stamps from
// the same icon overlapping each other via seeded rejection sampling.

// Stamps render as plain text (no chip), so the bbox is sized to the
// rough glyph extent rather than the old chip-with-padding footprint.
const STAMP_BOX_W = 22 * STAMP_SCALE;
const STAMP_BOX_H = 14 * STAMP_SCALE;

// Stamps must stay on the visible red cloth so they read as intended.
// The cloth is the triangle (cli-pathed fabric); a tiny negative margin
// allows organic-feeling extension right up to the edge but not past it
// onto the white canvas border.
function inCanvas(sl: number, st: number, sr: number, sb: number): boolean {
  const M = 0.5;
  return (
    st >= M &&
    sl >= sb / 2 + M &&
    sr <= 100 - sb / 2 - M
  );
}

// Approximate rendered bbox for a stamp. Year stacks of count > 1 render
// as a grid much larger than a single stamp, so the sampler needs the
// real footprint to avoid placing them off the cloth or onto neighbors.
// 1.3x pad covers the seeded rotation tilt (up to ~22 deg combined
// outer + grid rotation).
function stampBbox(stamp: StampDef): { w: number; h: number } {
  const ROT_PAD = 1.3;
  if (stamp.kind !== "year" || stamp.count === 1) {
    return { w: STAMP_BOX_W * ROT_PAD, h: STAMP_BOX_H * ROT_PAD };
  }
  const count = stamp.count;
  const cols = count <= 3 ? count : count <= 6 ? 2 : 3;
  const rows = Math.ceil(count / cols);
  const gap = 1 * STAMP_SCALE;
  return {
    w: (cols * STAMP_BOX_W + Math.max(0, cols - 1) * gap) * ROT_PAD,
    h: (rows * STAMP_BOX_H + Math.max(0, rows - 1) * gap) * ROT_PAD,
  };
}

function computeStampPositions(
  bboxes: Array<{ w: number; h: number }>,
  seed0: number,
  region: "top" | "left" | "right",
  slotLeft: number,
  slotTop: number,
): Array<{ dx: number; dy: number; rot: number }> {
  const placed: Array<{ x: number; y: number; w: number; h: number }> = [];
  const out: Array<{ dx: number; dy: number; rot: number }> = [];

  type Attempt = { dx: number; dy: number; rot: number; x: number; y: number };

  for (let si = 0; si < bboxes.length; si++) {
    const { w, h } = bboxes[si];
    const baseSeed = seed0 + si * 17 + 3;

    // Three-tier placement: prefer (1) in-canvas + non-colliding, then
    // fall back to (2) any in-canvas position, then to (3) the badge
    // center. This guarantees every stamp ends up on the red cloth.
    let chosen: Attempt | null = null;
    let canvasOnly: Attempt | null = null;

    for (let attempt = 0; attempt < 48; attempt++) {
      const s = baseSeed + attempt * 7;
      const { dx, dy } = stampOffset(s, region);
      const rot = (seededRand(s + 5) - 0.5) * 30;
      const x = CENTER_X + dx;
      const y = CENTER_Y + dy;
      const tryPos: Attempt = { dx, dy, rot, x, y };

      // Convert stamp bbox to CSS % and verify it's inside the triangle
      const sl = slotLeft + dx / W_REF * 100;
      const st = slotTop  + dy / H_REF * 100;
      const sr = sl + w / W_REF * 100;
      const sb = st + h / H_REF * 100;

      const inBounds = inCanvas(sl, st, sr, sb);
      const collides = placed.some(p =>
        rectsOverlap(x, y, w, h, p.x, p.y, p.w, p.h)
      );

      if (inBounds && !collides) {
        chosen = tryPos;
        break;
      }
      if (inBounds && !canvasOnly) {
        canvasOnly = tryPos;
      }
    }

    // Last-resort fallback: center the stamp on the badge so its full
    // footprint (including any grid extent) sits on red cloth even if
    // the rejection sampler couldn't find a clean spot.
    const final: Attempt = chosen ?? canvasOnly ?? {
      dx: -w / 2,
      dy: -h / 2,
      rot: 0,
      x: CENTER_X - w / 2,
      y: CENTER_Y - h / 2,
    };

    placed.push({ x: final.x, y: final.y, w, h });
    out.push({ dx: final.dx, dy: final.dy, rot: final.rot });
  }

  return out;
}

function rectsOverlap(
  ax: number, ay: number, aw: number, ah: number,
  bx: number, by: number, bw: number, bh: number,
): boolean {
  const pad = 2;
  return ax < bx + bw + pad &&
         ax + aw + pad > bx &&
         ay < by + bh + pad &&
         ay + ah + pad > by;
}

// ─── Activity node: circular embroidered-style patch ─────────────────────────

function ActivityNode({
  data,
  activityIndex,
  region,
  slotLeft,
  slotTop,
}: {
  data: KerchiefActivity;
  activityIndex: number;
  region: "top" | "left" | "right";
  slotLeft: number;
  slotTop: number;
}) {
  const stamps = buildStamps(data);
  const seed0 = activityIndex * 97 + data.activity.id * 53;
  const bboxes = stamps.map(stampBbox);
  const positions = computeStampPositions(bboxes, seed0, region, slotLeft, slotTop);

  return (
    <div style={{ width: NODE_W, position: "relative" }}>
      {/* Patch + name — flex column */}
      <div className="flex flex-col items-center gap-0.5" style={{ position: "relative", zIndex: 8 }}>
        {/* Patch — circular clip mask only */}
        <div
          style={{
            width: PATCH_SIZE,
            height: PATCH_SIZE,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {data.activity.thumbnailUrl ? (
            <img
              src={data.activity.thumbnailUrl}
              alt={data.activity.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Square2StackIcon style={{ width: PATCH_SIZE * 0.55, height: PATCH_SIZE * 0.55 }} />
          )}
        </div>
      </div>

      {/* Stamps — absolutely positioned on top of the patch. Same-year
          clusters render as a YearStack grid that carries its own tilt;
          the wrapper's rotate(rot) compounds with that to keep the
          organic, hand-stamped look. */}
      {stamps.map((stamp, si) => {
        const { dx, dy, rot } = positions[si];
        return (
          <div
            key={si}
            className="pointer-events-none"
            style={{
              position: "absolute",
              left: `${CENTER_X + dx}px`,
              top:  `${CENTER_Y + dy}px`,
              transform: `rotate(${rot}deg)`,
              zIndex: 20,
            }}
          >
            {stamp.kind === "year"       && <YearStack year={stamp.year} count={stamp.count} seed={seed0 + si * 31} />}
            {stamp.kind === "honor"      && <HonorBadge />}
            {stamp.kind === "graduation" && <GradBadge />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Digital Kerchief canvas ──────────────────────────────────────────────────
//
// Visually resembles a physical triangular kerchief lying on a surface:
//   • Drop shadow beneath the whole piece
//   • Thick fold-hem strip at top (darker crimson)
//   • Triangle body with fabric weave texture and gradient lighting
//   • Activity nodes styled as embroidered patches

// The 26 printed slots, clockwise — same positions as the Kerchief Layout
// editor. Each activity's slot comes from the backend (getCamperKerchief).
const SLOTS = computeClockwiseSlots();

function DigitalKerchief({ data }: { data: CamperKerchiefData }) {

  return (
    <div
      className="mx-auto select-none"
      style={{
        // Outer canvas extends past the cloth on each side so stamps that
        // scatter outside the triangle have visible space to land on.
        width: W_REF + 2 * CANVAS_BORDER_X,
        maxWidth: "100%",
        paddingLeft:  CANVAS_BORDER_X,
        paddingRight: CANVAS_BORDER_X,
        paddingBottom: CANVAS_BORDER_Y,
        // Outer drop-shadow — makes it look like physical cloth on a surface
        filter:
          "drop-shadow(0 10px 28px rgba(0,0,0,0.45)) drop-shadow(0 3px 6px rgba(0,0,0,0.25))",
      }}
    >
      {/* ── Fold hem ─────────────────────────────────────────────────────────
          Represents the thick rolled/folded top edge of a real kerchief.    */}
      <div
        style={{
          // Hem matches the cloth red so black sharpie ink reads on it.
          background: "linear-gradient(180deg, #a01818 0%, #b71c1c 55%, #c22020 100%)",
          padding: `${13 * SCALE * 0.7}px ${22 * SCALE * 0.7}px ${15 * SCALE * 0.7}px`,
          borderRadius: `${5 * SCALE * 0.5}px ${5 * SCALE * 0.5}px 0 0`,
          borderBottom: "3px solid rgba(0,0,0,0.30)",
          position: "relative",
          overflow: "hidden",
        }}
      >

        <div className="relative flex flex-wrap items-center justify-between" style={{ gap: 12 * SCALE * 0.5 }}>
          <span
            style={{
              // Sharpie-marker style: heavy display sans-serif, slightly
              // bled black ink, all caps.
              fontFamily: "'Marker Felt', 'Permanent Marker', 'Bradley Hand', 'Arial Black', sans-serif",
              fontSize: 16 * SCALE * 1.05,
              fontWeight: 900,
              color: "rgba(0,0,0,0.92)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              textShadow: "0 0 1.5px rgba(0,0,0,0.5)",
            }}
          >
            {data.camper.firstName} {data.camper.lastName}
          </span>
          <div className="flex flex-wrap" style={{ gap: 14 * SCALE * 0.5 }}>
            {data.yearsAttended.length === 0 ? (
              <span style={{ color: "rgba(0,0,0,0.75)", fontSize: 13 * SCALE * 0.7, fontStyle: "italic" }}>
                No enrollments recorded
              </span>
            ) : (
              data.yearsAttended.map((y) => (
                <span
                  key={y}
                  style={{
                    fontSize: 16 * SCALE * 1.0,
                    color: "rgba(0,0,0,0.92)",
                    fontFamily: "monospace",
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    textShadow: "0 0 1px rgba(0,0,0,0.55)",
                  }}
                >
                  {formatYear(y)}
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Triangle body ────────────────────────────────────────────────────
          The cloth itself is clipped to the triangle, but the activity
          layer is rendered as an unclipped sibling so stamps can extend
          past the triangle's diagonal edges into the surrounding canvas.  */}
      <div style={{ position: "relative", paddingBottom: "50%", overflow: "visible" }}>
        {/* Fabric background — clipped to triangle */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            clipPath: "polygon(0 0, 100% 0, 50% 100%)",
            // Layered backgrounds: fabric weave texture + radial lighting gradient
            backgroundImage: `
              repeating-linear-gradient(
                0deg,
                rgba(255,255,255,0.028) 0px, rgba(255,255,255,0.028) 1px,
                transparent 1px, transparent 5px
              ),
              repeating-linear-gradient(
                90deg,
                rgba(255,255,255,0.028) 0px, rgba(255,255,255,0.028) 1px,
                transparent 1px, transparent 5px
              ),
              radial-gradient(ellipse at 40% 18%, #cb2020 0%, #be1c1c 25%, #b01818 55%, #961515 85%, #821212 100%)
            `,
          }}
        >
          {/* SVG overlay inside the clipped triangle */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 50"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              {/* Top-to-bottom gradient: lighter at top (fold shadow) → slight darkening at tip */}
              <linearGradient id="triShading" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="rgba(255,255,255,0.10)" />
                <stop offset="30%"  stopColor="rgba(0,0,0,0)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0.18)" />
              </linearGradient>
              {/* Left-to-right slight sheen */}
              <linearGradient id="triSheen" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="rgba(255,255,255,0.06)" />
                <stop offset="50%"  stopColor="rgba(255,255,255,0.00)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0.06)" />
              </linearGradient>
            </defs>

            {/* Shading layers */}
            <polygon points="0,0 100,0 50,50" fill="url(#triShading)" />
            <polygon points="0,0 100,0 50,50" fill="url(#triSheen)" />

            {/* CH logo watermark — centered at triangle centroid (50, 16.7) */}
            <image
              href="/ch-logo.png"
              x="32" y="4"
              width="36" height="24"
              opacity="0.38"
              preserveAspectRatio="xMidYMid meet"
            />
          </svg>
        </div>

        {/* ── Activity layer ────────────────────────────────────────────────
            Sibling of the clipped fabric — uses the same coordinate
            system (paddingBottom: 50% rect) but no clip-path, so stamps
            scattered around each badge can land inside or outside the
            triangle itself. */}
        {data.activities.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <p style={{ color: "rgba(255,200,200,0.55)", fontSize: 14 }}>
              No activities recorded yet.
            </p>
          </div>
        ) : (
          data.activities.map((activity, idx) => {
            const pos = activity.slot != null ? SLOTS[activity.slot] : undefined;
            if (!pos) return null;
            return (
              <div
                key={`node-${activity.activity.id}`}
                className="absolute"
                style={{
                  left: `${pos.left}%`,
                  top:  `${pos.top}%`,
                  transform: "translate(-50%, -30%)",
                  zIndex: 10,
                  overflow: "visible",
                }}
              >
                <ActivityNode data={activity} activityIndex={idx} region={pos.region} slotLeft={pos.left} slotTop={pos.top} />
              </div>
            );
          })
        )}
      </div>

      {/* ── Legend ───────────────────────────────────────────────────────── */}
      <div className="mt-3 flex flex-wrap gap-5 text-xs text-stone-500">
        <span className="flex items-center gap-1.5">
          <span style={{ fontFamily: "Georgia, serif", fontWeight: 900, color: "#000" }}>H</span>
          Honor
        </span>
        <span className="flex items-center gap-1.5">
          <StarOutline size={14} />
          <span style={{ color: "#57534e" }}>Graduated this activity</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span style={{ fontFamily: "monospace", fontWeight: 800, color: "#000" }}>'24</span>
          Year AC earned
        </span>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function KerchiefView() {
  const [searchParams] = useSearchParams();
  const [camperId, setCamperId] = useState<number | null>(() => {
    const id = searchParams.get("camperId");
    return id ? Number(id) : null;
  });
  const [kerchiefData, setKerchiefData] = useState<CamperKerchiefData | null>(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // Sidebar collapse state
  const [sidebarOpen, setSidebarOpen]   = useState(true);

  // Zoom state
  const [scale, setScale]               = useState(1);
  const zoomRef                         = useRef<HTMLDivElement>(null);
  const contentRef                      = useRef<HTMLDivElement>(null);
  const pinchRef                        = useRef<{ dist: number } | null>(null);

  const clampScale = useCallback((s: number) => Math.min(3, Math.max(0.2, s)), []);

  const applyFitScale = useCallback(() => {
    if (!zoomRef.current || !contentRef.current) return;
    // Measure the inner DigitalKerchief div (max-w-3xl), not the transform wrapper
    // whose offsetWidth fills the whole container and gives incorrect natural dimensions.
    const inner = contentRef.current.firstElementChild as HTMLElement | null;
    const natW = inner?.offsetWidth ?? contentRef.current.offsetWidth;
    const natH = inner?.offsetHeight ?? contentRef.current.offsetHeight;
    if (!natW || !natH) return;
    const padding = 48;
    const availW = zoomRef.current.clientWidth - padding;
    const availH = zoomRef.current.clientHeight - padding;
    // Fill as much of the frame as possible; allow scaling below 100% to fit.
    setScale(Math.min(3, Math.max(0.2, Math.min(availW / natW, availH / natH))));
  }, []);

  // Wheel zoom
  useEffect(() => {
    const el = zoomRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScale(s => clampScale(s - e.deltaY * 0.001));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [clampScale]);

  // Pinch-to-zoom
  useEffect(() => {
    const el = zoomRef.current;
    if (!el) return;
    const pinchDist = (t: TouchList) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) pinchRef.current = { dist: pinchDist(e.touches) };
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !pinchRef.current) return;
      e.preventDefault();
      const newDist = pinchDist(e.touches);
      const ratio = newDist / pinchRef.current.dist;
      pinchRef.current.dist = newDist;
      setScale(s => clampScale(s * ratio));
    };
    const onTouchEnd = () => { pinchRef.current = null; };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [clampScale]);

  // Camper list sidebar
  const [allCampers, setAllCampers]     = useState<Camper[]>([]);
  const [listSearch, setListSearch]     = useState("");
  const [listLoading, setListLoading]   = useState(true);
  const listRef                         = useRef<HTMLDivElement>(null);

  useEffect(() => {
    campersApi
      .list({ pageSize: 500 })
      .then((res) => setAllCampers(res.data))
      .catch(() => toast.error("Failed to load camper list"))
      .finally(() => setListLoading(false));
  }, []);

  // Scroll selected camper into view when list loads or selection changes
  useEffect(() => {
    if (!camperId || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-camper-id="${camperId}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [camperId, allCampers]);

  const filteredCampers = allCampers.filter((c) =>
    `${c.firstName} ${c.lastName}`.toLowerCase().includes(listSearch.toLowerCase())
  );

  useEffect(() => {
    if (!camperId) { setKerchiefData(null); return; }
    setLoading(true);
    setError(null);
    reportsApi
      .camperKerchief(camperId)
      .then(setKerchiefData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load kerchief"))
      .finally(() => setLoading(false));
  }, [camperId]);

  // Auto-fit zoom when kerchief data is rendered
  useEffect(() => {
    if (!kerchiefData) return;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(applyFitScale);
    });
    return () => cancelAnimationFrame(id);
  }, [kerchiefData, applyFitScale]);

  // Re-fit after sidebar collapses (wait for CSS transition to complete)
  useEffect(() => {
    if (sidebarOpen) return;
    const id = setTimeout(applyFitScale, 310);
    return () => clearTimeout(id);
  }, [sidebarOpen, applyFitScale]);

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 64px)" }}>
      <PageHeader
        title="View Kerchief"
      />

      <div className="flex flex-1 min-h-0 relative">
        {/* ── Camper list sidebar ───────────────────────────────────────────── */}
        <aside
          className="flex-shrink-0 border-r border-stone-200 flex flex-col bg-white overflow-hidden transition-[width] duration-300 ease-in-out"
          style={{ width: sidebarOpen ? 256 : 0 }}
        >
          {/* Inner wrapper keeps content at full width while outer clips */}
          <div className="min-w-[256px] flex flex-col h-full">
            {/* Search input */}
            <div className="p-3 border-b border-stone-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  placeholder="Filter campers…"
                  className="block w-full pl-8 pr-3 py-2 rounded-md border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Scrollable list */}
            <div ref={listRef} className="flex-1 overflow-y-auto">
              {listLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredCampers.length === 0 ? (
                <p className="text-center text-stone-400 text-sm py-8 px-3">
                  {listSearch ? "No campers match." : "No campers found."}
                </p>
              ) : (
                filteredCampers.map((camper) => (
                  <button
                    key={camper.id}
                    data-camper-id={camper.id}
                    type="button"
                    onClick={() => { setCamperId(camper.id); setSidebarOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm border-b border-stone-100 transition-colors ${
                      camperId === camper.id
                        ? "bg-forest-50 text-forest-800 font-medium"
                        : "text-stone-700 hover:bg-stone-50"
                    }`}
                  >
                    {camper.lastName}, {camper.firstName}
                  </button>
                ))
              )}
            </div>
          </div>
        </aside>

        {/* ── Sidebar toggle tab ───────────────────────────────────────────── */}
        <button
          type="button"
          onClick={() => setSidebarOpen(s => !s)}
          title={sidebarOpen ? "Hide camper list" : "Show camper list"}
          className="absolute top-1/2 z-30 bg-white border border-stone-200 border-l-0 rounded-r-lg shadow-sm p-1 -translate-y-1/2 text-stone-500 hover:text-stone-800 transition-[left] duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-forest-500"
          style={{ left: sidebarOpen ? 256 : 0 }}
        >
          {sidebarOpen
            ? <ChevronLeftIcon  className="w-4 h-4" />
            : <ChevronRightIcon className="w-4 h-4" />
          }
        </button>

        {/* ── Main kerchief area ───────────────────────────────────────────── */}
        <main className="flex-1 overflow-hidden relative bg-stone-100">
          {loading && (
            <div className="flex items-center gap-3 text-stone-500 text-sm p-6">
              <div className="w-5 h-5 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
              Loading kerchief…
            </div>
          )}

          {error && <p className="text-red-600 text-sm p-6">{error}</p>}

          {!loading && !error && !kerchiefData && (
            <div className="flex flex-col items-center justify-center h-full text-stone-400">
              <p className="text-sm">Select a camper from the list to view their kerchief.</p>
            </div>
          )}

          {!loading && !error && kerchiefData && (
            <>
              {/* Zoom controls */}
              <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-white/80 backdrop-blur-sm border border-stone-200 rounded-lg shadow-sm px-2 py-1">
                <button
                  type="button"
                  onClick={() => setScale(s => clampScale(s - 0.15))}
                  className="w-6 h-6 flex items-center justify-center text-stone-600 hover:text-stone-900 font-bold text-base leading-none"
                  title="Zoom out"
                >−</button>
                <span className="text-xs text-stone-500 w-10 text-center tabular-nums select-none">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  type="button"
                  onClick={applyFitScale}
                  className="text-xs text-stone-500 hover:text-stone-800 px-1"
                  title="Fit to screen"
                >Fit</button>
                <button
                  type="button"
                  onClick={() => setScale(1)}
                  className="text-xs text-stone-500 hover:text-stone-800 px-1"
                  title="100%"
                >100%</button>
                <button
                  type="button"
                  onClick={() => setScale(s => clampScale(s + 0.15))}
                  className="w-6 h-6 flex items-center justify-center text-stone-600 hover:text-stone-900 font-bold text-base leading-none"
                  title="Zoom in"
                >+</button>
              </div>

              {/* Scrollable + zoomable kerchief canvas */}
              <div
                ref={zoomRef}
                className="h-full overflow-auto cursor-default"
                style={{ touchAction: "pan-x pan-y" }}
              >
                <div className="p-6 lg:p-8" style={{ minWidth: "fit-content" }}>
                  <div
                    ref={contentRef}
                    style={{
                      transformOrigin: "top center",
                      transform: `scale(${scale})`,
                      transition: "transform 0.05s ease-out",
                    }}
                  >
                    <DigitalKerchief data={kerchiefData} />
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
